import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useChecklistEntregaAmbienteListaFilters } from "../hooks/checklist_entrega_ambiente_lista/filters";
import { useChecklistEntregaAmbienteListaPagination } from "../hooks/checklist_entrega_ambiente_lista/pagination";
import { ChecklistEntregaAmbienteModel } from "../../domain/models/checklist_entrega_ambiente";
import { ChecklistEntregaAmbienteCadastroForm } from "../schemas/checklist_entrega_ambiente_lista/checklistEntregaAmbienteCadastro";
import { ChecklistEntregaAmbienteCopiaForm } from "../schemas/checklist_entrega_ambiente_lista/checklistEntregaAmbienteCopia";
import { useLocation } from "react-router";
import { ChecklistEntrega } from "../../domain/models/checklist_entrega";

type LoadChecklistEntregaAmbienteListaState = {
    idChecklistEntrega: number;
    descricao?: string,
    currentPage?: number;
    rowsPerPage?: number;
}

export const useChecklistEntregaAmbienteListaViewModel = () => {

    const {
        entregaObras: {
            getAllChecklistEntregaAmbienteUseCase,
            getChecklistEntregaAmbienteByIdUseCase,
            updateChecklistEntregaAmbienteUseCase,
            createChecklistEntregaAmbienteUseCase,
            copyChecklistEntregaAmbienteUseCase,
            deleteChecklistEntregaAmbienteUseCase,
            getChecklistEntregaByIdUseCase,
        },
        commons: {
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
        }
    } = useDependencyInjection();

    const backOrNavigate = useBackOrNavigate();

    const { pushErrorNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const debouncedRef = useCreateDebounceHandler(1500);

    const location = useLocation();
    const navigate = useBackOrNavigate();

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openCopiaModal, setOpenCopiaModal] = useState(false);
    const [openCadastroModal, setOpenCadastroModal] = useState(false);

    const [selectedIdChecklistEntregaAmbiente, setSelectedIdChecklistEntregaAmbiente] = useState<number>();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [checklistEntregaAmbienteLista, setChecklistEntregaAmbienteLista] = useState<ChecklistEntregaAmbienteModel[]>();
    const [selectedChecklistEntregaAmbiente, setSelectedChecklistEntregaAmbiente] = useState<ChecklistEntregaAmbienteModel | undefined>();
    const [selectedChecklistEntrega, setSelectedChecklistEntrega] = useState<ChecklistEntrega | undefined>();

    const [selectedIdChecklistEntrega, setSelectedIdChecklistEntrega] = useState<number | undefined>();

    const filters = useChecklistEntregaAmbienteListaFilters();
    const pagination = useChecklistEntregaAmbienteListaPagination();

    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification('Erro', [e]),
    })

    //#region CARREGAMENTO.

    const loadChecklistEntregaAmbienteLista = useCallback(async (funcState?: LoadChecklistEntregaAmbienteListaState) => {
        try {
            setLoading(true);
            const selectedIdChecklistEntregaFilter = funcState?.idChecklistEntrega ?? selectedIdChecklistEntrega;
            const filteredDescricao = funcState?.descricao ?? filters.descricao;

            const filteredPage = funcState?.currentPage ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllChecklistEntregaAmbienteUseCase.execute(
                selectedIdChecklistEntregaFilter ?? 0,
                filteredDescricao,
                filteredPage,
                filteredRowsPerPage,
            );

            setChecklistEntregaAmbienteLista(getAllChecklistEntregaAmbienteUseCase.items);

            pagination.setRowsPerPage(getAllChecklistEntregaAmbienteUseCase.pageSize);
            pagination.setTotalItems(getAllChecklistEntregaAmbienteUseCase.totalItems);
        } catch (e: unknown) {
            setChecklistEntregaAmbienteLista([]);
            pushErrorNotification("Error", processError(e, "Falha ao buscar lista de checklist de entrega!"));
        } finally {
            setLoading(false);
        }
    }, [
        filters,
        selectedIdChecklistEntrega,
        pagination,
        getAllChecklistEntregaAmbienteUseCase,
        pushErrorNotification
    ]);

    const loadChecklistEntregaAmbienteById = useCallback(async (idChecklist: number) => {
        try {
            setLoading(true);
            const checklist = await getChecklistEntregaAmbienteByIdUseCase.execute(idChecklist);
            setSelectedChecklistEntregaAmbiente(checklist);

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar Ambiente do checklist de entrega!'));
        } finally {
            setLoading(false);
        }
    }, [
        getChecklistEntregaAmbienteByIdUseCase,
        pushErrorNotification
    ]);

    const loadChecklistEntregaById = useCallback(async (idChecklistEntrega: number) => {
        try {
            setLoading(true);
            const checklist = await getChecklistEntregaByIdUseCase.execute(idChecklistEntrega);
            setSelectedChecklistEntrega(checklist);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar checklist de entrega!'));
        } finally {
            setLoading(false);
        }
    }, [
        getChecklistEntregaByIdUseCase,
        pushErrorNotification
    ]);

    //#endregion



    //#region PAGE START
    usePageStartBootstrap(async () => {
        try {
            const params = new URLSearchParams(location.search);
            const paramId = params.get('id');
            const locationId = paramId
                ? parseInt(paramId, 10)
                : undefined;

            if (locationId !== undefined) {
                setSelectedIdChecklistEntrega(locationId);
                await loadChecklistEntregaById(locationId);
                await loadChecklistEntregaAmbienteLista(
                    locationId ? { idChecklistEntrega: locationId } : undefined
                );
            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de checklist de entrega de ambiente!")[0]);
        }
    });

    const handleBack = useCallback(() => {
        navigate(`/ChecklistEntrega_lista`);
    }, [navigate]);

    //#endregion




    //#region NOVO/EDITAR.
    const handleChecklistEntregaAmbienteOpenState = useCallback(async (idChecklistEntregaAmbiente: number | null, openState: boolean) => {
        setOpenCadastroModal(openState);
        try {
            if (openState) {
                if (idChecklistEntregaAmbiente) {
                    await loadChecklistEntregaAmbienteById(idChecklistEntregaAmbiente);
                } else {
                    setSelectedChecklistEntregaAmbiente(undefined);
                    setSelectedIdChecklistEntregaAmbiente(undefined);
                }
            } else {
                setSelectedChecklistEntregaAmbiente(undefined);
                setSelectedIdChecklistEntregaAmbiente(undefined);
            }
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao iniciar criação de novo checklist de entrega de ambiente!'));
        }
    }, [
        loadChecklistEntregaAmbienteById,
        setOpenCadastroModal,
        pushErrorNotification
    ]);

    const handleSubmitChecklistEntregaAmbiente = useCallback(async (form: ChecklistEntregaAmbienteCadastroForm) => {
        try {
            setSaving(true);
            if (selectedChecklistEntregaAmbiente) {
                await updateChecklistEntregaAmbienteUseCase.execute(
                    selectedChecklistEntregaAmbiente?.idChecklistEntregaAmbiente ?? 0,
                    form.descricao,
                    form.ordem,
                );
                pushInfoNotification("Sucesso", " Ambiente do checklist de entrega atualizado com sucesso!", { isAutoFinish: true });
            } else {
                await createChecklistEntregaAmbienteUseCase.execute(
                    selectedIdChecklistEntrega ?? 0,
                    form.descricao,
                    form.ordem,
                );
                pushInfoNotification("Sucesso", " Ambiente do checklist de entrega criado com sucesso!", { isAutoFinish: true });
            }
            await loadChecklistEntregaAmbienteLista();
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar ambiente do Checklist de entrega!"));
        } finally {
            setSaving(false);
            setOpenCadastroModal(false);
            setSelectedChecklistEntregaAmbiente(undefined);
        }
    }, [
        selectedChecklistEntregaAmbiente,
        selectedIdChecklistEntrega,
        updateChecklistEntregaAmbienteUseCase,
        createChecklistEntregaAmbienteUseCase,
        loadChecklistEntregaAmbienteLista,
        pushErrorNotification,
        pushInfoNotification
    ]);

    //#endregion




    //#region PAGINAÇÃO.
    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadChecklistEntregaAmbienteLista();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de ambientes do checklist de entrega!'));
        } finally {
            setLoading(false);
        }
    }, [
        loadChecklistEntregaAmbienteLista,
        pushErrorNotification
    ]);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadChecklistEntregaAmbienteLista({ idChecklistEntrega: selectedIdChecklistEntrega ?? 0, currentPage: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de ambientes do checklist de entrega!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, selectedIdChecklistEntrega, loadChecklistEntregaAmbienteLista, pushErrorNotification]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadChecklistEntregaAmbienteLista({ idChecklistEntrega: selectedIdChecklistEntrega ?? 0, rowsPerPage, currentPage: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de ambientes do checklist de entrega!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, selectedIdChecklistEntrega, loadChecklistEntregaAmbienteLista, pushErrorNotification]);

    //#endregion



    //#region COPIA
    const handleChecklistEntregaAmbienteCopiaOpenState = useCallback((idChecklistEntregaAmbiente: number | null, openState: boolean) => {
        if (openState) {
            if (idChecklistEntregaAmbiente) {
                setSelectedIdChecklistEntregaAmbiente(idChecklistEntregaAmbiente);
                const checklistEntrega = checklistEntregaAmbienteLista?.find((item) => item.idChecklistEntregaAmbiente === idChecklistEntregaAmbiente);
                setSelectedChecklistEntregaAmbiente(checklistEntrega);
                setOpenCopiaModal(true);
            } else {
                setSelectedIdChecklistEntregaAmbiente(undefined);
                setSelectedChecklistEntregaAmbiente(undefined);
                setOpenCopiaModal(false);
            }
        }
        setOpenCopiaModal(openState);
    }, [
        checklistEntregaAmbienteLista,
    ]);

    const handleSubmitChecklistEntregaAmbienteCopia = useCallback(async (form: ChecklistEntregaAmbienteCopiaForm) => {
        try {
            setSaving(true);
            if (selectedIdChecklistEntregaAmbiente) {
                await copyChecklistEntregaAmbienteUseCase.execute(
                    selectedIdChecklistEntregaAmbiente,
                    form.ordem ?? undefined,
                    form.descricao ?? undefined
                );
                pushInfoNotification("Sucesso", " Ambiente do Checklist de entrega copiado com sucesso!", { isAutoFinish: true });
            } else {
                pushErrorNotification("Erro", "Não foi possivel copiar o Ambiente do Checklist de entrega!", { isAutoFinish: true });
            }
            await loadChecklistEntregaAmbienteLista();
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Copiar Ambiente do Checklist de entrega!"));
        } finally {
            setSaving(false);
            setOpenCopiaModal(false);
            setSelectedIdChecklistEntregaAmbiente(undefined)
        }
    }, [
        selectedIdChecklistEntregaAmbiente,
        copyChecklistEntregaAmbienteUseCase,
        loadChecklistEntregaAmbienteLista,
        pushErrorNotification,
        pushInfoNotification
    ]);

    //#endregion






    //#region DELECAO 
    const handleChecklistEntregaAmbienteDeleteOpenState = useCallback((idChecklistEntregaAmbiente: number | null, openState: boolean) => {
        if (openState) {
            if (idChecklistEntregaAmbiente) {
                setSelectedIdChecklistEntregaAmbiente(idChecklistEntregaAmbiente);
                setOpenDeleteModal(true);
            } else {
                setSelectedIdChecklistEntregaAmbiente(undefined);
                setOpenDeleteModal(false);
            }
        }
        setOpenDeleteModal(openState);
    }, []);

    const handleConfirmarDeletarChecklistEntregaAmbiente = useCallback(async () => {
        try {

            setSaving(true);
            if (selectedIdChecklistEntregaAmbiente) {
                await deleteChecklistEntregaAmbienteUseCase.execute(selectedIdChecklistEntregaAmbiente);
                pushInfoNotification("Sucesso", " Checklist de entrega deletado com sucesso!", { isAutoFinish: true });
            } else {
                pushErrorNotification("Erro", "Não foi possivel deletar Checklist de entrega!", { isAutoFinish: true });
            }
            await loadChecklistEntregaAmbienteLista()
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Deletar Checklist de entrega!"));
        } finally {
            setSaving(false);
            setOpenDeleteModal(false);
            setSelectedIdChecklistEntregaAmbiente(undefined)
        }
    }, [
        selectedIdChecklistEntregaAmbiente,
        deleteChecklistEntregaAmbienteUseCase,
        loadChecklistEntregaAmbienteLista,
        pushErrorNotification,
        pushInfoNotification
    ]);

    //#endregion





    //#region PESQUISA.
    const handleDescricaoChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {
            filters.setDescricao(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadChecklistEntregaAmbienteLista({ idChecklistEntrega: selectedIdChecklistEntrega ?? 0, descricao: newValue ?? undefined, currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar clientes!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, selectedIdChecklistEntrega, loadChecklistEntregaAmbienteLista, pushErrorNotification]
    );

    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadChecklistEntregaAmbienteLista({
                idChecklistEntrega: selectedIdChecklistEntrega ?? 0,
                descricao: '',
                currentPage: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar Ambientes do checklist de entrega!'));
        } finally {
            setLoading(false);
        }
    }, [
        filters,
        selectedIdChecklistEntrega,
        pagination,
        loadChecklistEntregaAmbienteLista,
        pushErrorNotification
    ]);

    //#endregion 



    //#region CRIAÇÃO/EDIÇÃO ITENS

    const handleEditItens = useCallback(async (id: number | null, openNewTab: boolean) => {
        if (id) {
            if (openNewTab) {
                window.open(`/ChecklistEntregaItens_cadastro?id=${id}`, "_blank", "noopener,noreferrer");;
            } else {
                backOrNavigate(`/ChecklistEntregaItens_cadastro?id=${id}`, { alternativeRoute: '/ChecklistEntregaItens_lista' });
            }
        } else {
            if (openNewTab) {
                window.open(`/ChecklistEntregaItens_cadastro`, '_blank', 'noopener,noreferrer');
            } else {
                backOrNavigate(`/ChecklistEntregaItens_cadastro`, { alternativeRoute: '/ChecklistEntregaItens_lista' });
            }
        }
    }, [
        backOrNavigate,
    ]);

    //#endregion



    //#region History
    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (true) {
                changeHistory.handleOpenChangeHistory(selectedChecklistEntregaAmbiente?.idChecklistEntregaAmbiente ?? 0, LogTipoTabela.CHECKLIST_ENTREGA_AMBIENTE, true);
            }
        }
    }, [
        selectedChecklistEntregaAmbiente,
        changeHistory,
    ]);

    //#endregion

    return {
        loading,
        saving,
        checklistEntregaAmbienteLista: checklistEntregaAmbienteLista ?? [],
        errorMessage,
        filters,
        pagination,
        openDeleteModal,
        selectedIdChecklistEntregaAmbiente,
        openCopiaModal,
        openCadastroModal,
        selectedChecklistEntregaAmbiente,
        changeHistory,
        selectedIdChecklistEntrega,
        selectedChecklistEntrega,

        handleBack,
        handleEditItens,
        handleOpenChangeHistory,
        handleSubmitChecklistEntregaAmbiente,
        handleChecklistEntregaAmbienteOpenState,
        handleChecklistEntregaAmbienteCopiaOpenState,
        handleSubmitChecklistEntregaAmbienteCopia,
        handleChecklistEntregaAmbienteDeleteOpenState,
        handleConfirmarDeletarChecklistEntregaAmbiente,
        handleDescricaoChange,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleClearFilters,
    };
}


